export const en = {
    CreateMenu: {
        newGoodVibe: "New Good Vibe",
        newFeedback: "New feedback"
    },
    Navigation: {
        home: {
            label: "Home",
            description: "Your daily brief"
        },
        "manager-impact": {
            label: "My impact as a manager"
        }
    },
    HomePage: {
        title: "Home",
        error: "We couldn't load your home dashboard right now.",
        liveRegion: {
            loading: "Loading…"
        }
    },
    Errors: {
        retry: "Try again",
        retrying: "Retrying…",
        reassurance: "Please try again later.",
        refreshBanner: "Couldn't refresh — showing the last results."
    },
    HomeAgentPanel: {
        greeting: {
            morning: "👋 Good morning, {{name}}.",
            afternoon: "👋 Good afternoon, {{name}}.",
            evening: "👋 Good evening, {{name}}."
        },
        inputLabel: "Ask your agent",
        inputPlaceholder: "Ask anything about your team or your company",
        sendLabel: "Send to your agent",
        quickPrompts: {
            teamDoing: "How is my team doing?",
            worthAttention: "What's worth my attention?",
            whoToRecognize: "Who should I recognize this week?"
        }
    },
    HomePerformanceCard: {
        badge: "Active review cycle",
        empty: "No active review cycle",
        opensInNewTab: "Opens in a new tab",
        rows: {
            selfReview: "Self reviews",
            managerReview: "Manager reviews",
            peerReview: "Peer reviews",
            upwardReview: "Upward reviews"
        }
    },
    HomePerformanceCarousel: {
        counter: "{{current}} of {{total}}",
        previous: "Previous cycle",
        next: "Next cycle"
    },
    HomeNewAndOutstanding: {
        heading: "New and outstanding",
        empty: "You're all caught up — nothing new for your team.",
        reviewsToWriteNoun_one: "review to write",
        reviewsToWriteNoun_other: "reviews to write",
        reviewsToWriteShowMore_one: "Show 1 more",
        reviewsToWriteShowMore_other: "Show {{count}} more",
        reviewsToWriteShowLess: "Show less",
        reviewerAssignmentsNoun_one: "reviewer assignment to make",
        reviewerAssignmentsNoun_other: "reviewer assignments to make",
        assignment: {
            peer: "Assign peers to review {{name}}",
            upward: "Assign upward reviewers for {{name}}",
            peerDue: "Peer review due {{date}}",
            upwardDue: "Upward review due {{date}}"
        },
        review: {
            self: "Your self review",
            managerOf: "Manager review of {{name}}",
            peerOf: "Peer review of {{name}}",
            upwardOf: "Upward review of {{name}}",
            due: "Due {{date}}"
        },
        badges: {
            unreadFeedback: "unread feedback messages",
            newGoodVibes_one: "new Good Vibe for me",
            newGoodVibes_other: "new Good Vibes for me",
            unseenReportsGoodVibes_one: "unseen Good Vibe for my reports",
            unseenReportsGoodVibes_other: "unseen Good Vibes for my reports",
            goalUpdates: "recent goal updates"
        }
    },
    HomeMyTeam: {
        heading: "My team",
        empty: "No team members",
        actions: {
            label: "Actions for {{name}}",
            sendGoodVibes: "Send Good Vibes",
            openNextOneOnOne: "Open next 1:1"
        },
        scope: {
            ariaLabel: "Reports scope",
            allReports: "All reports",
            directReports: "Direct reports"
        },
        emptyDirect: "No direct reports"
    },
    HomeBringYourTeamIn: {
        heading: "Bring your team in",
        description: "Sync from your HRIS, Slack, Microsoft, or Google to keep your team up to date. You choose who gets synced.",
        connectHris: "Connect your HRIS",
        addManually: "Or add people manually",
        uploadSpreadsheet: "Upload a spreadsheet",
        invitePeople: "Invite people",
        modalBody: "Setting up your team happens in Workleap Classic for now. We'll take you there. Nothing will be lost."
    },
    ManagerImpactReportPage: {
        title: "My impact as a manager",
        subtitle: "How you're impacting your direct reports.",
        betaBadge: "Beta",
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            home: "Home"
        },
        howToImprove: "How to improve",
        empty: "Your report isn't ready yet. Check back soon.",
        pillars: {
            heading: "Two pillars",
            subtitle: "Your overall score blends Practice (60%) and Outcome (40%)."
        },
        improveScorePrompt: "How can I improve my `{{scoreName}}` score? Give me specific actions I can take this week.",
        howToImprovePrompt: "How can I improve my impact as a manager? Tell me what to focus on.",
        liveRegion: {
            loading: "Loading…"
        },
        feedback: {
            heading: "How is this report?",
            description: "This report is new and still in beta. A quick read from you shapes what ships.",
            accurate: "This report is useful",
            inaccurate: "This report is not useful",
            placeholder: "Anything you would change? Optional.",
            submit: "Send feedback",
            thanks: "Thanks, we logged your feedback."
        }
    },
    ManagerImpactOverallScoreCard: {
        heading: "My latest management score",
        outOf: "/ {{max}}",
        status: {
            great: "Great",
            needsWork: "Needs work",
            noData: "Not enough data yet"
        }
    },
    ManagerImpactPillarCard: {
        practice: {
            title: "Practice",
            description: "What you control. Work on these now — they lift your score right away."
        },
        outcome: {
            title: "Outcome",
            description: "What follows. These rise only as your practice holds over time."
        },
        outOf: "/ {{max}}",
        composedOf: "Composed of",
        list: {
            ariaLabel: "Signals composing your {{pillar}} score"
        },
        status: {
            noData: "Not enough data yet"
        }
    },
    ManagerImpactComposingScoreRow: {
        status: {
            great: "Great",
            needsWork: "Needs work",
            noData: "Not enough data yet"
        },
        improveThisScore: "Get suggestions",
        signals: {
            "reply-rate-from-feedback": "Reply rate to feedback",
            "response-time-from-feedback": "Response time to feedback",
            "review-submission-rate": "Review submission rate",
            "goals-updated-hygiene": "Goal-update frequency",
            "recognition-sent": "Recognition given",
            "my-review-score": "My review score",
            "overall-review-score": "Overall review score",
            "enps-from-pulse": "Pulse eNPS",
            "goal-attainment": "Goal achievement",
            "relationship-with-manager": "Relationship with manager"
        },
        descriptions: {
            "reply-rate-from-feedback": "How much of your direct reports' feedback and messages you replied to in the past month.",
            "response-time-from-feedback": "How long, on average, you took to reply to your direct reports' feedback in the past month.",
            "review-submission-rate": "The share of your direct reports whose latest due review you submitted on time.",
            "goals-updated-hygiene": "The share of your team goals and your direct reports' goals that are kept up to date.",
            "recognition-sent": "How many of your direct reports you recognized in the last month.",
            "my-review-score": "Your own review score — the rating your leader (the person you report to) gave you. We rate this great when it reaches 70% of the review scale (for a 5-point scale, 3.5 out of 5).",
            "overall-review-score": "The average score across your direct reports' latest completed performance reviews. We rate this great when the average reaches 70% of the review scale (for a 5-point scale, 3.5 out of 5).",
            "enps-from-pulse": "How likely your direct reports are to recommend the company as a place to work.",
            "goal-attainment": "The share of goals reached, across goals owned by you or your direct reports.",
            "relationship-with-manager": "Your direct reports' relationship with manager score from the Pulse survey."
        }
    },
    ManagerImpactSignalInfo: {
        label: "About {{signal}}"
    },
    ManagerImpactLowConfidenceFlag: {
        label: "Low confidence — limited survey participation"
    },
    HomeManagerImpactCard: {
        liveRegion: {
            loading: "Loading your impact report"
        },
        eyebrow: "New report",
        cta: "My impact report",
        insight: {
            improve: "You can grow as a manager by focusing on {{opportunityLabel}}. Check your report to see how.",
            praise: "You're doing great as a manager. For an extra edge, look at {{opportunityLabel}}.",
            empty: "We're still gathering data on your direct reports. Check your report to see what's tracked."
        },
        opportunityLabels: {
            "reply-rate-from-feedback": "your reply rate to feedback",
            "response-time-from-feedback": "how quickly you reply to feedback",
            "review-submission-rate": "your review submission rate",
            "goals-updated-hygiene": "how often you keep goals updated",
            "recognition-sent": "how often you recognize your direct reports",
            "my-review-score": "your own review score",
            "overall-review-score": "your direct reports' review scores",
            "enps-from-pulse": "your direct reports' eNPS",
            "goal-attainment": "your direct reports' goal achievement",
            "relationship-with-manager": "your direct reports' relationship with you"
        }
    }
};
